import type { FolderDetails, TaskItem, TaskLikeNestedPayload } from "@/types";

/** List-item types that share task-style status/title UX and APIs. */
export const TASK_LIKE_ITEM_TYPES = [
  "TASK",
  "AI_TASK",
  "AI_TRANSCODE_TASK",
  "EPIC_TASK",
  "ORCHESTRATOR_TASK",
] as const;
export type TaskLikeItemType = (typeof TASK_LIKE_ITEM_TYPES)[number];

const TASK_LIKE_SET = new Set<string>(TASK_LIKE_ITEM_TYPES);

export function isTaskLikeItemType(
  itemType: string | null | undefined,
): boolean {
  if (!itemType) return false;
  return TASK_LIKE_SET.has(String(itemType).toUpperCase());
}

/** True when the list item is an EPIC_TASK. */
export function isEpicTaskItemType(
  itemType: string | null | undefined,
): boolean {
  return String(itemType || "").toUpperCase() === "EPIC_TASK";
}

/** True when the list item is an ORCHESTRATOR_TASK (backend-owned dummy task; never hosts children). */
export function isOrchestratorTaskItemType(
  itemType: string | null | undefined,
): boolean {
  return String(itemType || "").toUpperCase() === "ORCHESTRATOR_TASK";
}

/** True when this item type may host nested child list items. Always false for ORCHESTRATOR_TASK (leaf-only). */
export function canHostChildListItems(
  itemType: string | null | undefined,
): boolean {
  return !isOrchestratorTaskItemType(itemType);
}

/** True when the list item is an AI_TASK. */
export function isAiTaskItemType(
  itemType: string | null | undefined,
): boolean {
  return String(itemType || "").toUpperCase() === "AI_TASK";
}

/** True when the list item is AI-driven or otherwise backend-automated work (AI_TASK, AI_TRANSCODE_TASK, ORCHESTRATOR_TASK). */
export function isAiWorkItemType(
  itemType: string | null | undefined,
): boolean {
  const normalized = String(itemType || "").toUpperCase();
  return (
    normalized === "AI_TASK" ||
    normalized === "AI_TRANSCODE_TASK" ||
    normalized === "ORCHESTRATOR_TASK"
  );
}

/**
 * True when this item's status is always backend/AI driven and never set manually by a user
 * (EPIC_TASK, AI_TASK, AI_TRANSCODE_TASK). PENDING/IN_PROGRESS on these types are bounded,
 * self-resolving states — safe to poll until the backend moves them along. A plain TASK is
 * deliberately excluded: a human-assigned task can sit at PENDING indefinitely.
 */
export function isBackendDrivenTaskLikeItemType(
  itemType: string | null | undefined,
): boolean {
  return isEpicTaskItemType(itemType) || isAiWorkItemType(itemType);
}

/**
 * True when task status must not be edited or sent from the client.
 * A Workflow (an `EPIC_TASK`) and every task inside it are always status read-only —
 * workflow status progresses on its own, it isn't set manually from the client.
 */
export function isTaskStatusReadOnly(params: {
  itemType?: string | null;
  /** folderDetails.parent.itemType — child opened from a workflow */
  parentFolderItemType?: string | null;
  /** useItemStore.parentItemType — open folder is a workflow (child list view) */
  openFolderItemType?: string | null;
}): boolean {
  if (isEpicTaskItemType(params.itemType) || isOrchestratorTaskItemType(params.itemType)) {
    return true;
  }

  return (
    isEpicTaskItemType(params.parentFolderItemType) ||
    isEpicTaskItemType(params.openFolderItemType)
  );
}

/** `itemType` sent on update-task-status / related APIs (defaults to TASK). */
export function getTaskLikeListItemApiType(
  itemType: string | null | undefined,
): TaskLikeItemType {
  const u = String(itemType || "TASK").toUpperCase();
  return (TASK_LIKE_SET.has(u) ? u : "TASK") as TaskLikeItemType;
}

type ListItemWithTaskBranches = {
  itemType?: string;
  task?: TaskItem["task"] | null;
  aiTask?: TaskLikeNestedPayload | null;
  epicTask?: TaskLikeNestedPayload | null;
  aiTranscodeTask?: TaskLikeNestedPayload | null;
  orchestratorTask?: TaskLikeNestedPayload | null;
};

function coerceTaskPayload(
  raw: Pick<TaskItem["task"], "id" | "title" | "status"> &
    Partial<
      Pick<
        TaskItem["task"],
        "dueDate" | "assignedUser" | "startedAt" | "completedAt"
      >
    >,
): TaskItem["task"] {
  return {
    id: raw.id,
    title: raw.title,
    status: raw.status,
    dueDate: raw.dueDate ?? "",
    startedAt: raw.startedAt ?? "",
    completedAt: raw.completedAt ?? "",
    ...(raw.assignedUser ? { assignedUser: raw.assignedUser } : {}),
  };
}

function nestedPayloadToTaskRow(n: TaskLikeNestedPayload): TaskItem["task"] {
  return coerceTaskPayload({
    id: n.id,
    title: n.title,
    status: String(n.status),
    dueDate: n.dueDate ?? "",
    startedAt: n.startedAt ?? "",
    completedAt: n.completedAt ?? "",
  });
}

/**
 * Reads the nested task-like object from a list item (TASK / AI_TASK / AI_TRANSCODE_TASK / EPIC_TASK)
 * and returns the shape the task table and overview expect on `.task`.
 */
export function getTaskLikePayloadFromListItem(
  item: unknown,
): TaskItem["task"] | undefined {
  if (!item || typeof item !== "object") return undefined;
  const typed = item as ListItemWithTaskBranches;
  const type = String(typed.itemType || "").toUpperCase();

  if (type === "TASK" && typed.task) {
    return typed.task;
  }
  if (type === "AI_TASK" && typed.aiTask) {
    return nestedPayloadToTaskRow(typed.aiTask);
  }
  if (type === "AI_TRANSCODE_TASK" && typed.aiTranscodeTask) {
    return nestedPayloadToTaskRow(typed.aiTranscodeTask);
  }
  if (type === "EPIC_TASK" && typed.epicTask) {
    return nestedPayloadToTaskRow(typed.epicTask);
  }
  if (type === "ORCHESTRATOR_TASK" && typed.orchestratorTask) {
    return nestedPayloadToTaskRow(typed.orchestratorTask);
  }

  if (typed.task) return typed.task;
  if (typed.aiTask) {
    return nestedPayloadToTaskRow(typed.aiTask);
  }
  if (typed.epicTask) {
    return nestedPayloadToTaskRow(typed.epicTask);
  }
  if (typed.aiTranscodeTask) {
    return nestedPayloadToTaskRow(typed.aiTranscodeTask);
  }
  if (typed.orchestratorTask) {
    return nestedPayloadToTaskRow(typed.orchestratorTask);
  }
  return undefined;
}

/**
 * Human-readable label for a list-item or parent snippet from folder details
 * (folders use `name`; task-like types use nested task / epic / transcode titles when top-level `title` is missing).
 */
export function getListItemDisplayTitleFromListItemLike(item: unknown): string {
  if (!item || typeof item !== "object") return "";
  const row = item as { itemType?: string; name?: string; title?: string };
  const type = String(row.itemType || "").toUpperCase();
  if (type === "FOLDER") {
    return (row.name || "").trim();
  }
  const nested = getTaskLikePayloadFromListItem(item)?.title?.trim();
  if (nested) return nested;
  return String(row.title || row.name || "").trim();
}

/** UI-only: add/refresh `.task` so grouped task rows can use one code path. */
export function withNormalizedTaskFieldForGrouping<T extends object>(
  item: T,
): T & { task?: TaskItem["task"] } {
  const task = getTaskLikePayloadFromListItem(item);
  if (!task) return { ...item };
  return { ...item, task };
}

/** Folder detail / overview: expose unified `task` while preserving original `itemType` and nested objects. */
export function mergeTaskLikeFolderDetailsForOverview(
  data: FolderDetails,
): FolderDetails {
  const task = getTaskLikePayloadFromListItem(data);
  if (!task) return data;
  return { ...data, task };
}

type MutableTaskLike = ListItemWithTaskBranches & {
  task?: { status?: string };
  aiTask?: { status?: string };
  epicTask?: { status?: string };
  aiTranscodeTask?: { status?: string };
  orchestratorTask?: { status?: string };
};

/** Mutates nested status for React Query cache patches after update-task-status. */
export function applyStatusToTaskLikeListItem(
  item: unknown,
  nextStatus: string | undefined,
): boolean {
  if (!item || typeof item !== "object" || !nextStatus) return false;
  const typed = item as MutableTaskLike;
  const statusUpper = nextStatus.toUpperCase();
  const type = String(typed.itemType || "").toUpperCase();

  if (type === "TASK" && typed.task) {
    typed.task.status = statusUpper;
    return true;
  }
  if (type === "AI_TASK" && typed.aiTask) {
    typed.aiTask.status = statusUpper;
    return true;
  }
  if (type === "AI_TRANSCODE_TASK" && typed.aiTranscodeTask) {
    typed.aiTranscodeTask.status = statusUpper;
    return true;
  }
  if (type === "EPIC_TASK" && typed.epicTask) {
    typed.epicTask.status = statusUpper;
    return true;
  }
  if (type === "ORCHESTRATOR_TASK" && typed.orchestratorTask) {
    typed.orchestratorTask.status = statusUpper;
    return true;
  }

  if (typed.task) {
    typed.task.status = statusUpper;
    return true;
  }
  if (typed.aiTask) {
    typed.aiTask.status = statusUpper;
    return true;
  }
  if (typed.epicTask) {
    typed.epicTask.status = statusUpper;
    return true;
  }
  if (typed.aiTranscodeTask) {
    typed.aiTranscodeTask.status = statusUpper;
    return true;
  }
  if (typed.orchestratorTask) {
    typed.orchestratorTask.status = statusUpper;
    return true;
  }
  return false;
}

/**
 * After a mutation, keep list-item row canonical fields (`epicTask`, `aiTask`, …) in sync with the unified task view.
 */
export function applyTaskLikePayloadPatchToRow<T extends object>(
  row: T,
  patch: Partial<TaskItem["task"]>,
): T & { task: TaskItem["task"] } {
  const base =
    getTaskLikePayloadFromListItem(row) ??
    coerceTaskPayload({ id: 0, title: "", status: "PENDING" });
  const merged = { ...base, ...patch } as TaskItem["task"];
  const type = String(
    (row as ListItemWithTaskBranches).itemType || "",
  ).toUpperCase();
  const next = { ...row, task: merged } as T & {
    task: TaskItem["task"];
  } & ListItemWithTaskBranches;

  if (type === "EPIC_TASK" && next.epicTask) {
    next.epicTask = {
      ...next.epicTask,
      ...(patch.status != null ? { status: String(patch.status) } : {}),
      ...(patch.title != null ? { title: patch.title } : {}),
    };
  }
  if (type === "AI_TASK" && next.aiTask) {
    next.aiTask = {
      ...next.aiTask,
      ...(patch.status != null ? { status: String(patch.status) } : {}),
      ...(patch.title != null ? { title: patch.title } : {}),
    };
  }
  if (type === "AI_TRANSCODE_TASK" && next.aiTranscodeTask) {
    next.aiTranscodeTask = {
      ...next.aiTranscodeTask,
      ...(patch.status != null ? { status: String(patch.status) } : {}),
      ...(patch.title != null ? { title: patch.title } : {}),
    };
  }
  if (type === "ORCHESTRATOR_TASK" && next.orchestratorTask) {
    next.orchestratorTask = {
      ...next.orchestratorTask,
      ...(patch.status != null ? { status: String(patch.status) } : {}),
      ...(patch.title != null ? { title: patch.title } : {}),
    };
  }
  return next;
}

const TASK_LIKE_NESTED_KEYS = [
  "task",
  "aiTask",
  "epicTask",
  "aiTranscodeTask",
  "orchestratorTask",
] as const;

function omitNestedStatus(
  branch: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...branch };
  for (const key of TASK_LIKE_NESTED_KEYS) {
    const nested = next[key];
    if (nested && typeof nested === "object") {
      const { status: _status, ...rest } = nested as Record<string, unknown>;
      next[key] = rest;
    }
  }
  return next;
}

/**
 * Update-parent-child-item payloads are built by spreading raw `folderDetails` (which carries
 * `task`/`aiTask`/`epicTask`/`aiTranscodeTask.status` and an echoed `parent` snippet with the same
 * shape). This endpoint must never carry a status — that's `update-task-status`'s job — so strip
 * every nested `status` (including the flattened top-level one some callers spread on) before send.
 */
export function stripTaskLikeStatusFromPayload<T extends Record<string, unknown>>(
  payload: T,
): T {
  const { status: _status, ...rest } = omitNestedStatus(payload);
  const parent = rest.parent;
  if (parent && typeof parent === "object") {
    rest.parent = omitNestedStatus(parent as Record<string, unknown>);
  }
  return rest as T;
}
